import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getAssignment,
  getStudentSubmission,
  submitAssignment,
  updateSubmission
} from '../../api/educentialApi';
import './AssignmentView.css';

function AssignmentView() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [formData, setFormData] = useState({ textResponse: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    loadData();
  }, [assignmentId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load assignment details
      const assignmentResponse = await getAssignment(assignmentId);
      if (assignmentResponse.success) {
        setAssignment(assignmentResponse.data);
      }

      // Load student's submission if exists
      const submissionResponse = await getStudentSubmission(assignmentId);
      if (submissionResponse.success && submissionResponse.data) {
        setSubmission(submissionResponse.data);
        setFormData({ textResponse: submissionResponse.data.textResponse || '' });
      }
    } catch (error) {
      console.error('Error loading assignment:', error);
      showNotification('Failed to load assignment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleTextChange = (value) => {
    setFormData({ textResponse: value });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size
    if (file.size > assignment.maxFileSize * 1024 * 1024) {
      showNotification(`File size must be under ${assignment.maxFileSize}MB`, 'error');
      event.target.value = '';
      return;
    }

    // Validate file type
    if (assignment.allowedFileTypes) {
      const allowedTypes = assignment.allowedFileTypes.split(',').map(t => t.trim().toLowerCase());
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!allowedTypes.includes(fileExtension)) {
        showNotification(`File type .${fileExtension} is not allowed. Allowed types: ${assignment.allowedFileTypes}`, 'error');
        event.target.value = '';
        return;
      }
    }

    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (assignment.requireText && !formData.textResponse.trim()) {
        showNotification('Text response is required', 'error');
        return;
      }

      if (assignment.requireFile && !selectedFile && !submission?.fileUrl) {
        showNotification('File upload is required', 'error');
        return;
      }

      setSubmitting(true);

      let response;
      if (submission) {
        // Update existing submission
        response = await updateSubmission(submission.id, formData, selectedFile);
      } else {
        // Create new submission
        response = await submitAssignment(assignmentId, formData, selectedFile);
      }

      if (response.success) {
        showNotification(
          submission ? 'Submission updated successfully' : 'Assignment submitted successfully',
          'success'
        );
        setTimeout(() => {
          loadData(); // Reload to show updated submission
        }, 1000);
      } else {
        showNotification(response.error || 'Failed to submit assignment', 'error');
      }
    } catch (error) {
      console.error('Error submitting assignment:', error);
      showNotification(error.response?.data?.error || 'Failed to submit assignment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDueStatus = () => {
    if (!assignment.dueDate) return null;

    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const isLate = now > dueDate;
    const timeDiff = Math.abs(dueDate.getTime() - now.getTime());
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    if (isLate) {
      return { status: 'overdue', message: 'Assignment is overdue', className: 'status-overdue' };
    } else if (daysRemaining <= 1) {
      return { status: 'urgent', message: 'Due soon', className: 'status-urgent' };
    } else {
      return { status: 'upcoming', message: `${daysRemaining} days remaining`, className: 'status-upcoming' };
    }
  };

  const canEdit = () => {
    if (!submission) return true;
    return submission.status === 'SUBMITTED' || submission.status === 'RESUBMIT_REQUESTED';
  };

  if (loading) {
    return (
      <div className="assignment-view">
        <div className="loading-spinner">Loading assignment...</div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="assignment-view">
        <div className="error-message">Assignment not found</div>
      </div>
    );
  }

  const dueStatus = getDueStatus();

  return (
    <div className="assignment-view">
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="assignment-header">
        <div>
          <h1>{assignment.title}</h1>
          <p className="assignment-course">
            {assignment.Lesson?.Module?.Course?.title} • {assignment.Lesson?.title}
          </p>
        </div>
        <button onClick={() => navigate(-1)} className="btn-secondary">
          Back to Course
        </button>
      </div>

      {/* Assignment Info */}
      <div className="assignment-info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Maximum Score</span>
            <span className="info-value">{assignment.maxScore} points</span>
          </div>

          {assignment.dueDate && (
            <div className="info-item">
              <span className="info-label">Due Date</span>
              <span className="info-value">
                {formatDate(assignment.dueDate)}
                {dueStatus && (
                  <span className={`due-status ${dueStatus.className}`}>
                    {dueStatus.message}
                  </span>
                )}
              </span>
            </div>
          )}

          <div className="info-item">
            <span className="info-label">Late Submissions</span>
            <span className="info-value">
              {assignment.allowLateSubmission ? (
                <>
                  Allowed
                  {assignment.latePenalty && (
                    <span className="late-penalty"> (-{assignment.latePenalty}% per day)</span>
                  )}
                </>
              ) : (
                'Not allowed'
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Assignment Content */}
      <div className="assignment-content">
        {/* Description */}
        {assignment.description && (
          <div className="content-section">
            <h2>Description</h2>
            <p className="description-text">{assignment.description}</p>
          </div>
        )}

        {/* Instructions */}
        {assignment.instructions && (
          <div className="content-section">
            <h2>Instructions</h2>
            <div className="instructions-text">{assignment.instructions}</div>
          </div>
        )}

        {/* Rubric */}
        {assignment.rubric && (
          <div className="content-section">
            <h2>Grading Rubric</h2>
            <div className="rubric-text">{assignment.rubric}</div>
          </div>
        )}
      </div>

      {/* Submission Status */}
      {submission && (
        <div className="submission-status-card">
          <h2>Your Submission</h2>

          <div className="submission-meta">
            <div className="meta-item">
              <span className="meta-label">Status:</span>
              <span className={`status-badge status-${submission.status.toLowerCase()}`}>
                {submission.status.replace('_', ' ')}
              </span>
            </div>

            <div className="meta-item">
              <span className="meta-label">Submitted:</span>
              <span>{formatDate(submission.submittedAt)}</span>
              {submission.isLate && (
                <span className="late-indicator">
                  {' '}({submission.daysLate} day{submission.daysLate !== 1 ? 's' : ''} late)
                </span>
              )}
            </div>

            {submission.grade !== null && (
              <div className="meta-item">
                <span className="meta-label">Grade:</span>
                <span className="grade-display">
                  {submission.grade} / {assignment.maxScore}
                  {' '}({Math.round((submission.grade / assignment.maxScore) * 100)}%)
                </span>
              </div>
            )}
          </div>

          {submission.feedback && (
            <div className="feedback-section">
              <h3>Instructor Feedback</h3>
              <div className="feedback-text">{submission.feedback}</div>
            </div>
          )}

          {submission.status === 'RESUBMIT_REQUESTED' && (
            <div className="resubmit-notice">
              <strong>Resubmission Requested</strong>
              <p>Your instructor has requested that you resubmit this assignment. Please review the feedback above and submit a revised version.</p>
            </div>
          )}
        </div>
      )}

      {/* Submission Form */}
      {canEdit() && (
        <div className="submission-form-card">
          <h2>{submission ? 'Update Submission' : 'Submit Assignment'}</h2>

          {assignment.requireText && (
            <div className="form-group">
              <label>
                Text Response {assignment.requireText && <span className="required">*</span>}
              </label>
              <textarea
                value={formData.textResponse}
                onChange={(e) => handleTextChange(e.target.value)}
                className="form-control"
                rows="10"
                placeholder="Enter your response here..."
                disabled={submitting || (submission && submission.status === 'GRADED')}
              />
            </div>
          )}

          {(assignment.requireFile || !assignment.requireText) && (
            <div className="form-group">
              <label>
                File Upload {assignment.requireFile && <span className="required">*</span>}
              </label>

              {submission?.fileUrl && !selectedFile && (
                <div className="current-file">
                  <span className="file-name">{submission.fileName}</span>
                  <a
                    href={`${process.env.REACT_APP_API_BASE_URL || 'https://api.educential.com'}/${submission.fileUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-link"
                  >
                    Download
                  </a>
                </div>
              )}

              <input
                type="file"
                onChange={handleFileSelect}
                className="file-input"
                disabled={submitting || (submission && submission.status === 'GRADED')}
                accept={assignment.allowedFileTypes ? assignment.allowedFileTypes.split(',').map(t => `.${t.trim()}`).join(',') : ''}
              />

              {selectedFile && (
                <div className="selected-file">
                  <span className="file-name">{selectedFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="btn-remove"
                  >
                    Remove
                  </button>
                </div>
              )}

              <small className="help-text">
                {assignment.allowedFileTypes && `Allowed types: ${assignment.allowedFileTypes} • `}
                Max size: {assignment.maxFileSize}MB
              </small>
            </div>
          )}

          <div className="form-actions">
            <button
              onClick={handleSubmit}
              className="btn-primary"
              disabled={submitting || (submission && submission.status === 'GRADED')}
            >
              {submitting ? 'Submitting...' : submission ? 'Update Submission' : 'Submit Assignment'}
            </button>
          </div>

          {submission && submission.status === 'GRADED' && (
            <p className="graded-notice">
              This assignment has been graded and can no longer be edited.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default AssignmentView;
